import { useEffect, useRef } from 'react'

const insightImage = 'https://blueberry.azureedge.net/WillMars/sharper-insight.avif'
const structureImage = 'https://blueberry.azureedge.net/WillMars/Stronger_Structures.avif'
const decisionsImage = 'https://blueberry.azureedge.net/WillMars/confident-decisions.avif'

const SCENES = [
  {
    kicker: '01 · See the exposure clearly',
    title: ['Sharper', 'Insight'],
    body: 'Portfolio data, loss experience and accumulation detail are shaped into a clear view of the risks that matter.',
    image: insightImage,
    alt: 'Reinsurance professionals reviewing complex risk information',
  },
  {
    kicker: '02 · Build protection deliberately',
    title: ['Stronger', 'Structures'],
    body: 'Retention, capacity and market appetite are brought together in a programme built around the insurer’s objectives.',
    image: structureImage,
    alt: 'International team developing a reinsurance programme',
  },
  {
    kicker: '03 · Move with confidence',
    title: ['Confident', 'Decisions'],
    body: 'Clear analysis, disciplined negotiation and continued support help clients act with greater certainty.',
    image: decisionsImage,
    alt: 'Global team reaching an informed insurance decision',
  },
]

function clamp(value) {
  return Math.max(0, Math.min(1, value))
}

function mapRange(value, start, end) {
  return clamp((value - start) / Math.max(0.0001, end - start))
}

function StoryMotion() {
  const sectionRef = useRef(null)
  const stageRef = useRef(null)
  const sceneRefs = useRef([])
  const dotRefs = useRef([])
  const indexRef = useRef(null)

  useEffect(() => {
    const section = sectionRef.current
    const stage = stageRef.current
    if (!section || !stage || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    let ticking = false

    function render() {
      const scenes = sceneRefs.current.filter(Boolean)
      const dots = dotRefs.current.filter(Boolean)
      const count = scenes.length
      const wipeCount = Math.max(1, count - 1)
      const rect = section.getBoundingClientRect()
      const distance = Math.max(1, section.offsetHeight - window.innerHeight)
      const progress = clamp(-rect.top / distance)
      const scaled = progress * wipeCount

      stage.style.setProperty('--story-stage-width', `${stage.clientWidth}px`)

      let active = 0

      scenes.forEach((scene, index) => {
        const wipe = index === 0 ? 1 : clamp(scaled - (index - 1))
        const textIn = index === 0 ? 1 : mapRange(wipe, 0.32, 0.88)

        scene.style.setProperty('--wipe', wipe.toFixed(4))
        scene.style.setProperty('--text-in', textIn.toFixed(4))
        scene.style.setProperty('--scene-progress', wipe.toFixed(4))

        if (wipe >= 0.5) {
          active = index
        }
      })

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === active)
      })

      if (indexRef.current) {
        indexRef.current.textContent = String(active + 1).padStart(2, '0')
      }

      ticking = false
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(render)
        ticking = true
      }
    }

    function onNavClick(event) {
      const button = event.target.closest('[data-story-index]')
      if (!button) {
        return
      }
      const index = Number(button.dataset.storyIndex)
      const distance = Math.max(1, section.offsetHeight - window.innerHeight)
      const top = section.getBoundingClientRect().top + window.scrollY
      const target = top + (index / Math.max(1, SCENES.length - 1)) * distance
      window.scrollTo({ top: target, behavior: 'smooth' })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', render, { passive: true })
    stage.addEventListener('click', onNavClick)
    render()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', render)
      stage.removeEventListener('click', onNavClick)
    }
  }, [])

  return (
    <section className="story-motion" aria-label="How WillMars turns insight into action" ref={sectionRef}>
      <div className="story-stage" ref={stageRef}>
        {SCENES.map((scene, index) => (
          <article
            className={`story-scene${index === 0 ? ' is-active' : ''}`}
            key={scene.kicker}
            ref={(el) => { sceneRefs.current[index] = el }}
          >
            <div className="story-scene-inner">
              <div className="story-copy">
                <div className="story-kicker">{scene.kicker}</div>
                <h2 className="story-title">
                  <span>{scene.title[0]}</span>
                  <span>{scene.title[1]}</span>
                </h2>
                <p className="story-body">{scene.body}</p>
              </div>
              <figure className="story-media">
                <img src={scene.image} alt={scene.alt} />
              </figure>
            </div>
          </article>
        ))}
        <div className="story-nav">
          <div className="story-rail">
            {SCENES.map((scene, index) => (
              <button
                className={`story-dot${index === 0 ? ' is-active' : ''}`}
                data-story-index={index}
                key={scene.kicker}
                ref={(el) => { dotRefs.current[index] = el }}
                type="button"
                aria-label={`Go to scene ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
        <div className="story-index" aria-hidden="true"><span ref={indexRef}>01</span> / 03</div>
      </div>
    </section>
  )
}

export default StoryMotion
